import type { BoardRecord, FunnelBoardProject, JsonObject } from "../types";

export const MAX_PROJECT_BYTES = 10 * 1024 * 1024;
export const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
export const MAX_ELEMENTS = 5_000;
export const MAX_IMAGES = 50;

const ALLOWED_ELEMENT_TYPES = new Set([
  "selection",
  "rectangle",
  "diamond",
  "ellipse",
  "arrow",
  "line",
  "freedraw",
  "text",
  "image",
  "frame",
  "magicframe",
  "embeddable",
  "iframe",
  "laser",
]);

const ALLOWED_IMAGE_MIME_TYPES = new Set(["image/png", "image/jpeg", "image/webp", "image/gif"]);
const DANGEROUS_KEYS = new Set(["__proto__", "prototype", "constructor"]);
const TOP_LEVEL_KEYS = new Set(["format", "version", "exportedAt", "board"]);
const BOARD_KEYS = new Set(["id", "version", "name", "createdAt", "updatedAt", "templateId", "scene"]);
const SCENE_KEYS = new Set(["elements", "appState", "files"]);
const FILE_KEYS = new Set(["id", "dataURL", "mimeType", "created", "lastRetrieved"]);
const SAFE_APP_STATE_KEYS = new Set([
  "viewBackgroundColor",
  "gridSize",
  "gridStep",
  "gridModeEnabled",
  "scrollX",
  "scrollY",
  "zoom",
  "theme",
  "currentItemStrokeColor",
  "currentItemBackgroundColor",
  "currentItemFillStyle",
  "currentItemStrokeWidth",
  "currentItemStrokeStyle",
  "currentItemRoughness",
  "currentItemOpacity",
  "currentItemFontFamily",
  "currentItemFontSize",
  "currentItemTextAlign",
  "currentItemStartArrowhead",
  "currentItemEndArrowhead",
  "currentItemRoundness",
]);

export class InvalidProjectError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "InvalidProjectError";
  }
}

function isPlainObject(value: unknown): value is JsonObject {
  if (value === null || typeof value !== "object" || Array.isArray(value)) return false;
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

function assertPlainObject(value: unknown, label: string): asserts value is JsonObject {
  if (!isPlainObject(value)) throw new InvalidProjectError(`${label} mesti objek JSON yang sah.`);
}

function assertOnlyKeys(object: JsonObject, allowed: Set<string>, label: string): void {
  for (const key of Object.keys(object)) {
    if (DANGEROUS_KEYS.has(key) || !allowed.has(key)) {
      throw new InvalidProjectError(`${label} mengandungi medan tidak dijangka: ${key}.`);
    }
  }
}

function assertSafeTree(value: unknown, path: string, depth = 0): void {
  if (depth > 24) throw new InvalidProjectError(`${path} terlalu bersarang.`);
  if (typeof value === "string" && value.length > 1_000_000) {
    throw new InvalidProjectError(`${path} mengandungi teks yang terlalu panjang.`);
  }
  if (typeof value === "number" && !Number.isFinite(value)) {
    throw new InvalidProjectError(`${path} mengandungi nombor tidak sah.`);
  }
  if (Array.isArray(value)) {
    for (let index = 0; index < value.length; index += 1) {
      assertSafeTree(value[index], `${path}[${index}]`, depth + 1);
    }
    return;
  }
  if (isPlainObject(value)) {
    for (const [key, child] of Object.entries(value)) {
      if (DANGEROUS_KEYS.has(key)) throw new InvalidProjectError(`${path} mengandungi medan berbahaya.`);
      assertSafeTree(child, `${path}.${key}`, depth + 1);
    }
  }
}

function assertDate(value: unknown, label: string): asserts value is string {
  if (typeof value !== "string" || value.length > 40 || Number.isNaN(Date.parse(value))) {
    throw new InvalidProjectError(`${label} tidak sah.`);
  }
}

function validateElement(element: unknown, index: number): void {
  assertPlainObject(element, `Elemen ${index + 1}`);
  assertSafeTree(element, `Elemen ${index + 1}`);

  if (typeof element.id !== "string" || element.id.length < 1 || element.id.length > 200) {
    throw new InvalidProjectError(`Elemen ${index + 1} tidak mempunyai ID yang sah.`);
  }
  if (typeof element.type !== "string" || !ALLOWED_ELEMENT_TYPES.has(element.type)) {
    throw new InvalidProjectError(`Jenis elemen ${index + 1} tidak disokong.`);
  }
  for (const key of ["x", "y", "width", "height"] as const) {
    const value = element[key];
    if (typeof value !== "number" || !Number.isFinite(value) || Math.abs(value) > 10_000_000) {
      throw new InvalidProjectError(`Koordinat elemen ${index + 1} tidak sah.`);
    }
  }
  if ("link" in element && element.link !== null) {
    throw new InvalidProjectError("Pautan dalam elemen tidak dibenarkan.");
  }
  if (element.type === "embeddable" || element.type === "iframe") {
    throw new InvalidProjectError("Kandungan terbenam tidak dibenarkan.");
  }
}

function estimatedDataUrlBytes(dataUrl: string): number {
  const comma = dataUrl.indexOf(",");
  if (comma === -1) return Number.POSITIVE_INFINITY;
  return Math.ceil((dataUrl.length - comma - 1) * 0.75);
}

function validateFiles(value: unknown): Record<string, unknown> {
  assertPlainObject(value, "Fail imej");
  const entries = Object.entries(value);
  if (entries.length > MAX_IMAGES) throw new InvalidProjectError(`Maksimum ${MAX_IMAGES} imej dibenarkan.`);

  for (const [key, file] of entries) {
    if (key.length > 200 || DANGEROUS_KEYS.has(key)) throw new InvalidProjectError("ID imej tidak sah.");
    assertPlainObject(file, `Imej ${key}`);
    assertOnlyKeys(file, FILE_KEYS, `Imej ${key}`);
    if (typeof file.mimeType !== "string" || !ALLOWED_IMAGE_MIME_TYPES.has(file.mimeType)) {
      throw new InvalidProjectError(`Jenis fail imej ${key} tidak dibenarkan.`);
    }
    if (typeof file.dataURL !== "string") throw new InvalidProjectError(`Data imej ${key} tidak sah.`);
    const expectedPrefix = `data:${file.mimeType};base64,`;
    if (!file.dataURL.startsWith(expectedPrefix)) {
      throw new InvalidProjectError(`Data imej ${key} mesti Base64 setempat.`);
    }
    if (estimatedDataUrlBytes(file.dataURL) > MAX_IMAGE_BYTES) {
      throw new InvalidProjectError(`Imej ${key} melebihi had 5 MB.`);
    }
  }

  return value;
}

function sanitizeAppState(value: unknown): Record<string, unknown> {
  assertPlainObject(value, "Tetapan kanvas");
  const clean: Record<string, unknown> = {};
  for (const [key, child] of Object.entries(value)) {
    if (DANGEROUS_KEYS.has(key)) throw new InvalidProjectError("Tetapan kanvas mengandungi medan berbahaya.");
    if (!SAFE_APP_STATE_KEYS.has(key)) continue;
    assertSafeTree(child, `Tetapan kanvas.${key}`);
    clean[key] = child;
  }
  clean.theme = "light";
  clean.viewBackgroundColor = "#f8fafc";
  return clean;
}

export function parseProjectJson(raw: string): FunnelBoardProject {
  if (new TextEncoder().encode(raw).byteLength > MAX_PROJECT_BYTES) {
    throw new InvalidProjectError("Fail projek melebihi had 10 MB.");
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw) as unknown;
  } catch {
    throw new InvalidProjectError("Fail bukan JSON yang sah.");
  }

  assertPlainObject(parsed, "Projek");
  assertOnlyKeys(parsed, TOP_LEVEL_KEYS, "Projek");
  if (parsed.format !== "funnelboard" || parsed.version !== 1) {
    throw new InvalidProjectError("Format atau versi projek tidak disokong.");
  }
  assertDate(parsed.exportedAt, "Tarikh eksport");

  assertPlainObject(parsed.board, "Papan");
  assertOnlyKeys(parsed.board, BOARD_KEYS, "Papan");
  const board = parsed.board;
  if (board.version !== 1) throw new InvalidProjectError("Versi papan tidak disokong.");
  if (typeof board.id !== "string" || board.id.length < 1 || board.id.length > 200) {
    throw new InvalidProjectError("ID papan tidak sah.");
  }
  if (typeof board.name !== "string" || board.name.trim().length < 1 || board.name.length > 80) {
    throw new InvalidProjectError("Nama papan tidak sah.");
  }
  assertDate(board.createdAt, "Tarikh ciptaan");
  assertDate(board.updatedAt, "Tarikh kemas kini");
  if (board.templateId !== undefined && (typeof board.templateId !== "string" || board.templateId.length > 100)) {
    throw new InvalidProjectError("ID templat tidak sah.");
  }

  assertPlainObject(board.scene, "Kandungan papan");
  assertOnlyKeys(board.scene, SCENE_KEYS, "Kandungan papan");
  if (!Array.isArray(board.scene.elements)) throw new InvalidProjectError("Senarai elemen tidak sah.");
  if (board.scene.elements.length > MAX_ELEMENTS) {
    throw new InvalidProjectError(`Maksimum ${MAX_ELEMENTS} elemen dibenarkan.`);
  }
  board.scene.elements.forEach(validateElement);

  const safeBoard: BoardRecord = {
    id: board.id,
    version: 1,
    name: board.name.trim(),
    createdAt: board.createdAt,
    updatedAt: board.updatedAt,
    ...(typeof board.templateId === "string" ? { templateId: board.templateId } : {}),
    scene: {
      elements: board.scene.elements,
      appState: sanitizeAppState(board.scene.appState),
      files: validateFiles(board.scene.files),
    },
  };

  return {
    format: "funnelboard",
    version: 1,
    exportedAt: parsed.exportedAt,
    board: safeBoard,
  };
}

export async function readProjectFile(file: File): Promise<FunnelBoardProject> {
  if (file.size > MAX_PROJECT_BYTES) throw new InvalidProjectError("Fail projek melebihi had 10 MB.");
  if (!file.name.toLowerCase().endsWith(".json")) throw new InvalidProjectError("Hanya fail .json dibenarkan.");
  return parseProjectJson(await file.text());
}

